import React, { useState, useEffect, useRef } from 'react';
import { fetchGames, fetchPredictions, fetchEventStats, fetchEventLineup, fetchLiveScores, fetchGameUpdates, checkCacheStatus, fetchBoxScore } from './api';
import './GameList.css';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import Tooltip from './components/Tooltip';

// Feature flag for confidence indicator - set to false to hide confidence badges
const ENABLE_CONFIDENCE_INDICATOR = false;

// Helper function to get today's date in YYYY-MM-DD format (handles timezone properly)
const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Helper function to validate date string
const isValidDate = (dateString) => {
    if (!dateString || dateString === '') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString.match(/^\d{4}-\d{2}-\d{2}$/);
};

// Helper function to get safe date with fallback
const getSafeDate = (dateString, fallback = null) => {
    if (isValidDate(dateString)) {
        return dateString;
    }
    if (fallback && isValidDate(fallback)) {
        return fallback;
    }
    return getTodayDate();
};

// League logo URLs - fallback logos if API doesn't provide them
const LEAGUE_LOGOS = {
    'NBA': 'https://cdn.nba.com/logos/nba/1610612737/primary/L/logo.svg',
    'PremierLeague': 'https://upload.wikimedia.org/wikipedia/en/f/f2/Premier_League_Logo.png'
};

const formatLeagueName = (leagueName) => {
    if (leagueName === 'PremierLeague') {
        return 'Premier League';
    }
    return leagueName;
};

// Function to get league logo URL
const getLeagueLogo = (league, leagueBadge) => {
    // First try to use the league badge from API
    if (leagueBadge && leagueBadge.trim() !== '') {
        return leagueBadge;
    }
    
    // Fallback to our predefined logos
    return LEAGUE_LOGOS[league] || '';
};

// Function to format league and status for compact display
const formatLeagueAndStatus = (league, status) => {
    // Abbreviate league names
    let leagueAbbr = league;
    if (league === 'PremierLeague') {
        leagueAbbr = 'PL';
    } else if (league === 'NBA') {
        leagueAbbr = 'NBA';
    }
    
    // Abbreviate status
    let statusAbbr = status;
    if (status === 'Match Finished') {
        statusAbbr = 'FT';
    } else if (status === 'Full Time') {
        statusAbbr = 'FT';
    } else if (status === 'Final') {
        statusAbbr = 'FT';
    } else if (status === 'Finished') {
        statusAbbr = 'FT';
    } else if (status === 'Half Time') {
        statusAbbr = 'HT';
    } else if (status === 'First Half') {
        statusAbbr = '1H';
    } else if (status === 'Second Half') {
        statusAbbr = '2H';
    } else if (status === 'Extra Time') {
        statusAbbr = 'ET';
    } else if (status === 'Penalties') {
        statusAbbr = 'PEN';
    } else if (status === 'AOT') {
        statusAbbr = 'AOT';
    } else if (status === 'AET') {
        statusAbbr = 'AET';
    }
    
    return `${leagueAbbr} • ${statusAbbr}`;
};

// Function to check if a game is live
const isLiveGame = (gameStatus) => {
    const liveStatuses = ['Live', 'HT', '1H', '2H', 'ET', 'PEN', 'Extra Time', 'Penalties'];
    return liveStatuses.includes(gameStatus);
};

// Function to check if this is a high confidence prediction
const isHighConfidence = (cachedConfidence) => {
    // cachedConfidence is max(home_prob, away_prob) - the winner's probability
    // Real confidence is distance from 50%
    const realConfidence = Math.abs(cachedConfidence - 0.5);
    
    if (realConfidence >= 0.12) { // 62%+ probability
        return { 
            isHigh: true, 
            label: 'High Confidence', 
            color: '#00C851', 
            percentage: Math.round(cachedConfidence * 100) 
        };
    } else {
        return { isHigh: false };
    }
};

const GameList = () => {
    const [games, setGames] = useState([]);
    const [predictions, setPredictions] = useState([]);
    const [liveScores, setLiveScores] = useState([]);
    const [showScores, setShowScores] = useState(true);
    const [isLoading, setIsLoading] = useState(true); // Add loading state
    const [hasLoadedData, setHasLoadedData] = useState(false); // Track if data has been loaded before
    const [isPreloading, setIsPreloading] = useState(false); // Track preloading state
    const [hasAutoNavigated, setHasAutoNavigated] = useState(false); // Track if we've auto-navigated
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    
    // Safe date initialization with validation
    const urlDate = queryParams.get('date');
    const [filterDate, setFilterDate] = useState(getSafeDate(urlDate));
    
    const [filterTeam, setFilterTeam] = useState('');
    const [filteredGames, setFilteredGames] = useState([]);
    const [filterLeague, setFilterLeague] = useState(queryParams.get('league') || '');
    const navigate = useNavigate();
    
    // Cache reference to prevent unnecessary re-renders
    const dataCache = useRef({ games: [], predictions: [], liveScores: [], lastFetch: null });
    const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache

    // Function to find the most recent date with completed games
    const findMostRecentDateWithGames = () => {
        if (games.length === 0) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day

        // Filter for completed games (games that are in the past or today)
        const completedGames = games.filter(game => {
            const gameDate = new Date(game.date);
            gameDate.setHours(0, 0, 0, 0);
            return gameDate <= today;
        });

        if (completedGames.length === 0) {
            return;
        }

        // Get all unique dates from completed games and sort them in descending order
        const gameDates = [...new Set(completedGames.map(game => game.date))].sort((a, b) => {
            return new Date(b) - new Date(a);
        });

        if (gameDates.length > 0) {
            const mostRecentDate = gameDates[0];
            
            // Navigate to the most recent date with completed games
            setFilterDate(mostRecentDate);
            setFilterTeam('');
            setFilterLeague('');
            navigate(`?date=${mostRecentDate}&league=`);
            setHasAutoNavigated(true);
        }
    };

    // Preload data on component mount for better perceived performance
    useEffect(() => {
        const preloadData = async () => {
            if (!isPreloading && !hasLoadedData) {
                setIsPreloading(true);
                try {
                    // Start preloading games immediately (predictions can wait for user interaction)
                    const gameData = await fetchGames();
                    if (Array.isArray(gameData)) {
                        setGames(gameData);
                        setHasLoadedData(true);
                        
                        // Note: Cache warming is now handled by scheduled background processes
                        // Users should not trigger expensive cache warming operations
                        // This ensures better performance and prevents resource waste
                    }
                } catch (error) {
                    console.error('Error preloading games:', error);
                } finally {
                    setIsPreloading(false);
                }
            }
        };
        
        preloadData();
    }, [isPreloading, hasLoadedData]);

    // Auto-navigate to most recent date with games when games are loaded
    useEffect(() => {
        if (games.length > 0 && !hasAutoNavigated) {
            // Check if URL already has a specific date parameter
            const urlDate = queryParams.get('date');
            
            if (!urlDate || urlDate === '') {
                // No date in URL, auto-navigate to most recent date with games
                findMostRecentDateWithGames();
            } else {
                // URL has a date, check if it has games
                const urlDateHasGames = games.some(game => game.date === urlDate);
                if (!urlDateHasGames) {
                    // URL date has no games, navigate to most recent date with games
                    findMostRecentDateWithGames();
                } else {
                    // URL date has games, mark as auto-navigated to prevent further navigation
                    setHasAutoNavigated(true);
                }
            }
        }
    }, [games, hasAutoNavigated, queryParams]);

    useEffect(() => {
        const loadData = async () => {
            // Check if we can use cached data
            const now = Date.now();
            const timeSinceLastFetch = dataCache.current.lastFetch ? now - dataCache.current.lastFetch : Infinity;
            
            if (timeSinceLastFetch < CACHE_DURATION && hasLoadedData) {
                // Use cached data for fast loading
                setGames(dataCache.current.games);
                setPredictions(dataCache.current.predictions);
                setLiveScores(dataCache.current.liveScores);
                setIsLoading(false);
                return;
            }
            
            // Only show loading if we haven't loaded data before
            if (!hasLoadedData) {
                setIsLoading(true);
            }
            
            try {
                // Fetch games, predictions, and live scores in parallel for better performance
                // Request full current season predictions (2024-2025 season)
                // NBA season runs October to June, so we need ~12 month range to catch full season
                const startDate = moment().subtract(12, 'months').startOf('month').format('YYYY-MM-DD');
                const endDate = moment().add(6, 'months').endOf('month').format('YYYY-MM-DD');
                
                const [gameData, predictionData, liveScoresData] = await Promise.allSettled([
                    fetchGames(),
                    fetchPredictions(startDate, endDate),
                    fetchLiveScores() // Fetch all live scores
                ]);

                if (gameData.status === 'fulfilled' && Array.isArray(gameData.value)) {
                    setGames(gameData.value);
                    dataCache.current.games = gameData.value;
                }
                if (predictionData.status === 'fulfilled' && Array.isArray(predictionData.value)) {
                    setPredictions(predictionData.value);
                    dataCache.current.predictions = predictionData.value;
                }
                if (liveScoresData.status === 'fulfilled' && Array.isArray(liveScoresData.value)) {
                    setLiveScores(liveScoresData.value);
                    dataCache.current.liveScores = liveScoresData.value;
                }
                
                // Update cache timestamp
                dataCache.current.lastFetch = now;
                
                // Mark that we've successfully loaded data
                setHasLoadedData(true);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setIsLoading(false); // End loading regardless of success/failure
            }
        };
        loadData();
    }, [hasLoadedData]);

    // Listen for URL changes (back/forward navigation)
    useEffect(() => {
        const currentParams = new URLSearchParams(location.search);
        const urlDate = currentParams.get('date');
        const urlLeague = currentParams.get('league') || '';
        
        // Safely handle date parameter with validation
        const safeDate = getSafeDate(urlDate);
        
        // Update state to match URL parameters
        setFilterDate(safeDate);
        setFilterLeague(urlLeague);
        // Clear team filter on navigation (since it's not in URL)
        setFilterTeam('');
        
        // If the URL date was invalid, update the URL with the safe date
        if (urlDate && !isValidDate(urlDate)) {
            console.warn(`Invalid date in URL: ${urlDate}, correcting to: ${safeDate}`);
            navigate(`?date=${safeDate}&league=${urlLeague}`, { replace: true });
        }
    }, [location.search, navigate]);

    useEffect(() => {
        const filtered = games.filter((game) => {
            return (
                (filterDate ? game.date === filterDate : true) &&
                (filterTeam
                    ? game.home_team.toLowerCase().includes(filterTeam.toLowerCase()) ||
                      game.away_team.toLowerCase().includes(filterTeam.toLowerCase())
                    : true) &&
                (filterLeague ? game.league === filterLeague : true)
            );
        });
        setFilteredGames(filtered);
    }, [games, filterDate, filterTeam, filterLeague]);

    const handleDateChange = (selectedDate) => {
        const safeDate = getSafeDate(selectedDate);
        setFilterDate(safeDate);
        navigate(`?date=${safeDate}&league=${filterLeague}`);
    };

    const handleLeagueChange = (e) => {
        const selectedLeague = e.target.value;
        setFilterLeague(selectedLeague);
        navigate(`?date=${filterDate}&league=${selectedLeague}`);
    };

    const handleTeamChange = (e) => {
        setFilterTeam(e.target.value);
    };

    const toggleShowScores = () => {
        setShowScores(!showScores);
    };

    const changeDate = (offset) => {
        const currentDate = isValidDate(filterDate) ? new Date(filterDate) : new Date();
        currentDate.setDate(currentDate.getDate() + offset);
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const newDate = `${year}-${month}-${day}`;
        setFilterDate(newDate);
        navigate(`?date=${newDate}&league=${filterLeague}`);
    };

    const findPreviousDayWithGames = () => {
        // Don't search if games data isn't loaded yet
        if (games.length === 0) {
            return;
        }
        
        const currentDate = isValidDate(filterDate) ? new Date(filterDate) : new Date();
        
        // Search backwards up to 30 days to find a day with games
        for (let i = 1; i <= 30; i++) {
            const searchDate = new Date(currentDate);
            searchDate.setDate(currentDate.getDate() - i);
            const year = searchDate.getFullYear();
            const month = String(searchDate.getMonth() + 1).padStart(2, '0');
            const day = String(searchDate.getDate()).padStart(2, '0');
            const searchDateString = `${year}-${month}-${day}`;
            
            // Check if there are games on this date (ignoring team/league filters)
            const gamesOnDate = games.filter(game => game.date === searchDateString);
            
            if (gamesOnDate.length > 0) {
                // Found games! Navigate to this date and clear filters
                setFilterDate(searchDateString);
                setFilterTeam('');
                setFilterLeague('');
                navigate(`?date=${searchDateString}&league=`);
                return;
            }
        }
        
        // If no games found in the last 30 days, just go back one day
        changeDate(-1);
    };

    const findNextDayWithGames = () => {
        // Don't search if games data isn't loaded yet
        if (games.length === 0) {
            return;
        }
        
        const currentDate = isValidDate(filterDate) ? new Date(filterDate) : new Date();
        
        // Search forwards up to 30 days to find a day with games
        for (let i = 1; i <= 30; i++) {
            const searchDate = new Date(currentDate);
            searchDate.setDate(currentDate.getDate() + i);
            const year = searchDate.getFullYear();
            const month = String(searchDate.getMonth() + 1).padStart(2, '0');
            const day = String(searchDate.getDate()).padStart(2, '0');
            const searchDateString = `${year}-${month}-${day}`;
            
            // Check if there are games on this date (ignoring team/league filters)
            const gamesOnDate = games.filter(game => game.date === searchDateString);
            
            if (gamesOnDate.length > 0) {
                // Found games! Navigate to this date and clear filters
                setFilterDate(searchDateString);
                setFilterTeam('');
                setFilterLeague('');
                navigate(`?date=${searchDateString}&league=`);
                return;
            }
        }
        
        // If no games found in the next 30 days, just go forward one day
        changeDate(1);
    };

    const findPrediction = (game) => {
        return predictions.find(
            (prediction) => {
                // Check if teams match in either orientation (home/away might be swapped)
                const teamsMatch = (
                    (prediction.home_team === game.home_team && prediction.away_team === game.away_team) ||
                    (prediction.home_team === game.away_team && prediction.away_team === game.home_team)
                );
                
                // Check if dates match with ±1 day flexibility (like box scores)
                const gameDate = moment(game.date);
                const predictionDate = moment(prediction.date);
                
                // Try exact date first, then ±1 day
                const datesMatch = gameDate.isSame(predictionDate, 'day') ||
                                  gameDate.isSame(predictionDate.clone().add(1, 'day'), 'day') ||
                                  gameDate.isSame(predictionDate.clone().subtract(1, 'day'), 'day');
                
                return teamsMatch && datesMatch;
            }
        );
    };

    const getHighlightClass = (homeProb, awayProb) => {
        if (homeProb > awayProb) return 'highlight-home';
        if (awayProb > homeProb) return 'highlight-away';
        return '';
    };

    const handleGameClick = async (eventId) => {
        try {
            const eventStats = await fetchEventStats(eventId);
            const eventLineup = await fetchEventLineup(eventId);
            const gameData = games.find(game => game.event_id === eventId);
            const prediction = gameData ? findPrediction(gameData) : null;

            navigate(`/event/${eventId}`, { state: { eventStats, eventLineup, game: gameData, prediction } });
        } catch (error) {
            console.error('Failed to fetch event details:', error.message);
            alert('Unable to fetch event details. Please try again later.');
        }
    };

    return (
        <div className="game-list-container">
            <h1 className="game-list-title">Games</h1>
            <div className="filters">
                <div className="date-filter">
                    <button className="date-nav-button" onClick={() => changeDate(-1)} disabled={isLoading}>
                        <BsArrowLeft />
                    </button>
                    <input type="date" value={filterDate} onChange={(e) => handleDateChange(e.target.value)} />
                    <button className="date-nav-button" onClick={() => changeDate(1)} disabled={isLoading}>
                        <BsArrowRight />
                    </button>
                </div>
                <input type="text" placeholder="Filter by team..." value={filterTeam} onChange={handleTeamChange} />
                <button onClick={toggleShowScores} className="score-toggle">
                    {showScores ? 'Hide Scores' : 'Show Scores'}
                </button>
            </div>
            <div className="league-filter">
                <select value={filterLeague} onChange={handleLeagueChange}>
                    <option value="">All Leagues</option>
                    <option value="NBA">NBA</option>
                    <option value="PremierLeague">Premier League</option>
                </select>
            </div>
            {isLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading games...</p>
                </div>
            ) : (
                <ul className="list-group game-list-group">
                    {Array.isArray(filteredGames) && filteredGames.length > 0 ? (
                        filteredGames.map((game, index) => {
                            const prediction = findPrediction(game);
                            const highlightClass =
                                prediction && getHighlightClass(prediction.home_win_prob, prediction.away_win_prob);
                            const confidenceInfo = ENABLE_CONFIDENCE_INDICATOR && prediction ? isHighConfidence(prediction.confidence) : null;

                            // Check if this game is live
                            const isLive = isLiveGame(game.status);
                            // Check if game is completed (includes various completion statuses)
                            const isCompleted = ['Match Finished', 'FT', 'Full Time', 'Final', 'Finished', 'AOT', 'AET'].includes(game.status);
                            const shouldShowScore = showScores && (isCompleted || isLive);

                            return (
                                <li
                                    key={index}
                                    className={`list-group-item game-list-group-item ${highlightClass || ''}`}
                                    onClick={() => handleGameClick(game.event_id)}
                                >
                                    <div className="game-card">
                                        {/* League and Status Header */}
                                        <div className="game-header">
                                            <span className="league-status">
                                                {(() => {
                                                    const leagueLogo = getLeagueLogo(game.league, game.league_badge);
                                                    return (
                                                        <>
                                                            {leagueLogo && leagueLogo.trim() !== '' && (
                                                                <img 
                                                                    src={leagueLogo} 
                                                                    alt={`${formatLeagueName(game.league)} logo`}
                                                                    className="league-logo"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            )}
                                                            {formatLeagueAndStatus(game.league, game.status)}
                                                        </>
                                                    );
                                                })()}
                                            </span>
                                        </div>
                                        <div className="game-row">
                                            <div className="team-info">
                                                <img
                                                    src={game.home_team_badge}
                                                    alt={`${game.home_team} Badge`}
                                                    className="team-badge"
                                                />
                                                <strong>{game.home_team}</strong>
                                            </div>
                                            <div className="score-column">
                                                <span className={`team-score ${!shouldShowScore ? 'hidden' : ''}`}>
                                                    {shouldShowScore ? game.home_score : '?'}
                                                </span>
                                            </div>
                                            {prediction && (
                                                <div className="win-prob-container">
                                                    <span className="prob-label">Win Prob.</span>
                                                    <span
                                                        className={`win-prob home ${
                                                            highlightClass === 'highlight-home' ? 'favored' : ''
                                                        }`}
                                                    >
                                                        {prediction && prediction.home_win_prob !== undefined 
                                                            ? `${Math.round(prediction.home_win_prob * 100)}%` 
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="game-divider"></div>
                                        <div className="game-row">
                                            <div className="team-info">
                                                <img
                                                    src={game.away_team_badge}
                                                    alt={`${game.away_team} Badge`}
                                                    className="team-badge"
                                                />
                                                <strong>{game.away_team}</strong>
                                            </div>
                                            <div className="score-column">
                                                <span className={`team-score ${!shouldShowScore ? 'hidden' : ''}`}>
                                                    {shouldShowScore ? game.away_score : '?'}
                                                </span>
                                            </div>
                                            {prediction && (
                                                <div className="win-prob-container">
                                                    <span
                                                        className={`win-prob away ${
                                                            highlightClass === 'highlight-away' ? 'favored' : ''
                                                        }`}
                                                    >
                                                        {prediction && prediction.away_win_prob !== undefined 
                                                            ? `${Math.round(prediction.away_win_prob * 100)}%` 
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="game-details">
                                            {prediction && game.league !== 'NBA' && (
                                                <span className="draw-prob">
                                                    Draw: {prediction && prediction.draw_prob !== undefined 
                                                        ? `${Math.round(prediction.draw_prob * 100)}%` 
                                                        : 'N/A'}
                                                </span>
                                            )}
                                            {confidenceInfo && confidenceInfo.isHigh && (
                                                <div className="confidence-indicator">
                                                    <span 
                                                        className="confidence-badge confidence-high"
                                                        style={{ borderColor: confidenceInfo.color }}
                                                    >
                                                        <span className="confidence-dot" style={{ backgroundColor: confidenceInfo.color }}></span>
                                                        {confidenceInfo.label}
                                                    </span>
                                                </div>
                                            )}
                                            <Tooltip 
                                                content="Get RODMAN AI's prediction and analysis for this matchup"
                                                position="top"
                                                delay={300}
                                            >
                                                <button
                                                    className="ask-rodman-btn"
                                                    onClick={async (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();

                                                        try {
                                                            // Determine if this is an NBA game and if it's completed
                                                            const isNBAGame = game.league === 'NBA';
                                                            const isUpcoming = game.status === 'Not Started' || game.status === 'NS';
                                                            
                                                            // Prepare promises for parallel fetching
                                                            const fetchPromises = [
                                                                fetchEventStats(game.event_id),
                                                                fetchEventLineup(game.event_id)
                                                            ];
                                                            
                                                            // Add box score fetch for completed NBA games
                                                            if (isNBAGame && !isUpcoming) {
                                                                fetchPromises.push(fetchBoxScore(game.home_team, game.away_team, game.date));
                                                            }
                                                            
                                                            const results = await Promise.all(fetchPromises);
                                                            const [eventStats, eventLineup, boxScore] = results;

                                                            // Create enriched game context with full event data
                                                            const enrichedGameContext = {
                                                                ...game,
                                                                prediction: prediction,
                                                                eventStats: eventStats,
                                                                eventLineup: eventLineup,
                                                                boxScore: isNBAGame && !isUpcoming ? boxScore : null
                                                            };

                                                            const presetQuery = isUpcoming
                                                                ? `Give me your prediction for ${game.home_team} vs ${game.away_team}`
                                                                : `Analyze the ${game.home_team} vs ${game.away_team} game - what happened? They scored ${game.home_score}-${game.away_score}.`;

                                                            navigate('/rodman', {
                                                                state: {
                                                                    gameContext: enrichedGameContext,
                                                                    gameString: `${game.home_team} vs ${game.away_team}`,
                                                                    league: game.league,
                                                                    presetQuery: presetQuery,
                                                                    autoStart: true
                                                                }
                                                            });
                                                        } catch (error) {
                                                            console.error('Failed to fetch event data for Rodman:', error);

                                                            const fallbackContext = {
                                                                ...game,
                                                                prediction: prediction,
                                                                boxScore: null // No box score in fallback
                                                            };

                                                            const isUpcomingFallback = game.status === 'Not Started' || game.status === 'NS';
                                                            const presetQuery = isUpcomingFallback
                                                                ? `Give me your prediction for ${game.home_team} vs ${game.away_team}`
                                                                : `Analyze the ${game.home_team} vs ${game.away_team} game - what happened? They scored ${game.home_score}-${game.away_score}.`;

                                                            navigate('/rodman', {
                                                                state: {
                                                                    gameContext: fallbackContext,
                                                                    gameString: `${game.home_team} vs ${game.away_team}`,
                                                                    league: game.league,
                                                                    presetQuery: presetQuery,
                                                                    autoStart: true
                                                                }
                                                            });
                                                        }
                                                    }}
                                                >
                                                    ⚡ Ask RODMAN
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </li>
                            );
                        })
                    ) : (
                        <div className="no-games-message">
                            <p>No games scheduled for {moment(filterDate).format('MMMM D, YYYY')}.</p>
                            <div className="no-games-actions">
                                <button className="date-nav-button" onClick={findPreviousDayWithGames} disabled={isLoading}>
                                    <BsArrowLeft /> Find Previous Games
                                </button>
                            </div>
                        </div>
                    )}
                </ul>
            )}
        </div>
    );
};

export default GameList;